import type _jquery from "jquery";
import { type MaybeRefOrGetter, provide } from "vue";
import {
  injectRequiredWithSelf,
  checkValuesAlreadyProvided,
  CreateInjectionKeysRecord,
  type InjectionKeysRecord,
  type InjectionValuesRecord,
} from "@mcwd/util-vue";
import { useGatingLevel } from "./use-gating-level.js";
import type { FormTrackingName } from "../form-settings/form-experience/form-experience-settings.js";
import { useFormLogger } from "./use-form-logger.js";
import { useFormEvents } from "./use-form-events.js";
import { FormAbandonmentManager } from "../form-logic/FormAbandonmentManager.js";
import type { InputState } from "./use-input-state.js";
import type { FormWidgetData } from "../../widgetDataTs.js";
import { useHiddenFieldInitializer } from "./use-hidden-field-initializer.js";


type UseFormAbandonmentInjectionValues = InjectionValuesRecord<{
  abandonmentManager: FormAbandonmentManager
}>;

type UseFormAbandonmentInjectionKeys = InjectionKeysRecord<UseFormAbandonmentInjectionValues>;

const UseFormAbandonmentInjectionKeys = CreateInjectionKeysRecord<UseFormAbandonmentInjectionKeys>({
  abandonmentManager: "abandonmentManager"
});
/**
 * Initiates the class Manager that handles Form abandonment logic
 */
export function initFormAbandonmentManager({ formName, isInModal, inputStates, widgetData }: {
  formName: FormTrackingName;
  isInModal: MaybeRefOrGetter<boolean>;
  inputStates: Record<string, InputState>;
  widgetData: FormWidgetData,
}) {

  if (checkValuesAlreadyProvided(UseFormAbandonmentInjectionKeys)) {
    return;
  }
  const { CurrentMarketoFormGatingState } = useGatingLevel({
    callerLabel: "initFormAbandonmentManager",
  });
  const { hiddenFieldValueBuilder } = useHiddenFieldInitializer({ callerLabel: "initFormAbandonmentManager" });
  // Creates the Form Abandonment Class
  // starts tracking field history
  const abandonmentManager = new FormAbandonmentManager({
    formName,
    gatingState: CurrentMarketoFormGatingState.value,
    isInModal,
    inputStates,
    widgetData,
    hiddenFieldValue: hiddenFieldValueBuilder.hiddenFieldValuesObject
  });
  const events = useFormEvents({ callerLabel: "initFormTracking" });
  //const { logger } = useFormLogger();

  const formAbandonmentHandler = ()=>{
    events.formAbandonment.off(formAbandonmentHandler);
    abandonmentManager.captureLastInputState();
    abandonmentManager.sendAbandonmentData();
  };
  // when the form abandonment event is triggers than we collect and parsed
  // all the data to send to GA4
  events.formAbandonment.on(formAbandonmentHandler);

  provide(UseFormAbandonmentInjectionKeys.abandonmentManager, abandonmentManager);
}
/**
 * Most likely not necessary to use since all of this logic takes place at the end of 
 * the forms lifecycle but some of the data that is recorded might be useful
 * in other places.
 */
export function useFormAbandonmentManager({ callerLabel }: { callerLabel?: string | undefined } = {}) {
  const { logger } = useFormLogger();
  try {
    callerLabel ??= "FormAbandonmentManager";

    const abandonmentManager = injectRequiredWithSelf(UseFormAbandonmentInjectionKeys.abandonmentManager);
    return { abandonmentManager };
  } catch (err) {
    logger.error(`InjectionError from ${callerLabel ?? "unknown"}`);
    throw err;
  }
}